import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION = 'lessonPlaybackProgress';

const MIN_RESUME_SECONDS = 3;
const END_THRESHOLD_SECONDS = 5;

interface PlaybackProgressDoc {
  userId: string;
  lessonId: string;
  draftId: string;
  virtualTime: number;
  totalDuration: number;
  updatedAt: string;
}

function docId(userId: string, draftId: string): string {
  return `${userId}_${draftId}`;
}

// ── localStorage cache ─────────────────────────────────────────────────────
// localStorage.setItem is synchronous and survives hard refresh — unlike an
// async Firestore write which races the browser's navigation and may not
// complete. We write here first (before the await setDoc) so the position is
// always preserved even when the page is torn down immediately afterward.

function lsKey(userId: string, draftId: string): string {
  return `db_pbp_${userId}_${draftId}`;
}

function lsWrite(userId: string, draftId: string, virtualTime: number, totalDuration: number): void {
  try {
    localStorage.setItem(lsKey(userId, draftId), JSON.stringify({ virtualTime, totalDuration }));
  } catch {}
}

function lsRead(userId: string, draftId: string): number | null {
  try {
    const raw = localStorage.getItem(lsKey(userId, draftId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { virtualTime?: number; totalDuration?: number };
    const { virtualTime, totalDuration } = parsed;
    if (typeof virtualTime !== 'number') return null;
    if (virtualTime < MIN_RESUME_SECONDS) return null;
    if ((totalDuration ?? 0) > 0 && virtualTime > (totalDuration ?? 0) - END_THRESHOLD_SECONDS) return null;
    return virtualTime;
  } catch { return null; }
}

function lsClear(userId: string, draftId: string): void {
  try { localStorage.removeItem(lsKey(userId, draftId)); } catch {}
}

// ── Public API ────────────────────────────────────────────────────────────

/**
 * Synchronously returns the device-local saved position. Use this on mount
 * to show an instant resume point before the async Firestore fetch completes.
 */
export function getLocalPlaybackProgress(userId: string, draftId: string): number | null {
  return lsRead(userId, draftId);
}

/**
 * Synchronously writes the position to localStorage only (no Firestore).
 * Call this on every position update — localStorage writes are cheap and
 * need no throttling. Firestore writes should be separately throttled.
 */
export function saveLocalPlaybackProgress(
  userId: string,
  draftId: string,
  virtualTime: number,
  totalDuration: number,
): void {
  lsWrite(userId, draftId, virtualTime, totalDuration);
}

/**
 * Returns the saved virtual-timeline position in seconds, or null if there is
 * nothing worth restoring (no doc, position at start, or position near end).
 * Reads from Firestore — use getLocalPlaybackProgress for an instant result.
 */
export async function getPlaybackProgress(
  userId: string,
  draftId: string,
): Promise<number | null> {
  const ref = doc(db, COLLECTION, docId(userId, draftId));
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;

  const data = snap.data() as PlaybackProgressDoc;
  const { virtualTime, totalDuration } = data;

  if (virtualTime < MIN_RESUME_SECONDS) return null;
  if (totalDuration > 0 && virtualTime > totalDuration - END_THRESHOLD_SECONDS) return null;

  return virtualTime;
}

/**
 * Persists the current playback position. Writes to localStorage first
 * (synchronous, survives hard refresh), then to Firestore (cross-device).
 */
export async function savePlaybackProgress(
  userId: string,
  draftId: string,
  lessonId: string,
  virtualTime: number,
  totalDuration: number,
): Promise<void> {
  // Synchronous — completes before JS yields, survives hard refresh.
  lsWrite(userId, draftId, virtualTime, totalDuration);

  // Async — cross-device source of truth.
  const ref = doc(db, COLLECTION, docId(userId, draftId));
  const data: PlaybackProgressDoc = {
    userId,
    lessonId,
    draftId,
    virtualTime,
    totalDuration,
    updatedAt: new Date().toISOString(),
  };
  await setDoc(ref, data);
}

/**
 * Removes the stored position after the user watches to the end, so that the
 * next visit starts from the beginning.
 */
export async function clearPlaybackProgress(
  userId: string,
  draftId: string,
): Promise<void> {
  lsClear(userId, draftId);
  const ref = doc(db, COLLECTION, docId(userId, draftId));
  await deleteDoc(ref);
}
